import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { invoke } from '@tauri-apps/api/core'
import { useAuthStore } from '../store'
import { reportHarmonyPushToken } from '../hooks'
import { saveAuthData } from '../../api/secureStore'
import { tauriApi } from '../../api/tauriApi'
import { User } from '../store'
import { isPasswordCompliant, PASSWORD_POLICY_HINT } from '@shared/services/passwordPolicy'

/**
 * 跨端登录设置页（首次华为登录强制引导，不可跳过）。
 * 统一单输入框自适应流程：
 *   1. 输入邮箱（唯一输入框）→ 服务端探测该邮箱是否已被注册
 *   2a. 未注册 → 设置密码完成注册（其它客户端邮箱密码登录同一账号）
 *   2b. 已注册 → 提示合并：输入该账号密码 → 华为身份迁移映射到该账号
 * 邮箱必须真实（网页 / 桌面端靠它登录；之后可在"我的帐号"中更换）。
 * 无"跳过"出口，仅提供"退出登录"（保证每个华为用户都有跨端凭证）。
 */
export const EmailBindingPage: React.FC = () => {
  const navigate = useNavigate()
  const {
    isAuthenticated,
    user,
    accessToken,
    refreshToken,
    expiresAt,
    setUser,
    setTokens,
  } = useAuthStore()
  // step：'email' 唯一邮箱输入 → 'create' 新邮箱设密 → 'existing' 已注册邮箱合并
  const [step, setStep] = useState<'email' | 'create' | 'existing'>('email')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [confirm, setConfirm] = useState('')
  const [busy, setBusy] = useState(false)
  const [message, setMessage] = useState<{ ok: boolean; text: string } | null>(null)

  // 未认证 → 回登录页
  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login', { replace: true })
    }
  }, [isAuthenticated, navigate])

  // 防御：已无绑定需求（如其它端已完成）→ 回主页
  useEffect(() => {
    if (isAuthenticated && user && !user.needsEmailBinding) {
      navigate('/', { replace: true })
    }
  }, [isAuthenticated, user, navigate])

  // ★ 键盘遮挡修复（三版，2026-10-28）：鸿蒙 WebView 键盘弹出不收缩布局视口，
  //   scrollIntoView 无处可滚（bridge_contract 已证伪），改为聚焦时卡片整体上移：
  //   pointer: coarse（触屏）→ keyboardUp → 布局从垂直居中改为顶部对齐，
  //   输入框落在键盘上方可视区；切步骤/提交成功时复位
  const [keyboardUp, setKeyboardUp] = useState(false)
  useEffect(() => {
    setKeyboardUp(false)
  }, [step])

  const handleInputFocus = () => {
    if (window.matchMedia?.('(pointer: coarse)').matches) {
      setKeyboardUp(true)
    }
  }

  /** 更新内存 + 安全存储中的 user（needsEmailBinding 持久化） */
  const persistUpdatedUser = async (updated: User): Promise<void> => {
    setUser(updated)
    if (accessToken) {
      await saveAuthData({
        accessToken,
        refreshToken: refreshToken || '',
        expiresAt: expiresAt || 0,
        user: {
          id: updated.id,
          email: updated.email,
          name: updated.name,
          nickname: updated.nickname,
          avatar: updated.avatar,
          needsEmailBinding: updated.needsEmailBinding,
        },
      }).catch((e: unknown) => {
        console.warn('[EmailBinding] 持久化认证数据失败:', e)
      })
    }
  }

  const apiBase = (import.meta as any).env?.VITE_API_BASE_URL || 'http://localhost:4000'

  /** 第一步：探测邮箱是否已注册，据此进入"设密注册"或"验证合并"分支 */
  const handleCheckEmail = async () => {
    if (busy || !user) return
    const trimmed = email.trim()
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
      setMessage({ ok: false, text: '邮箱格式不正确' })
      return
    }
    if (!accessToken) {
      setMessage({ ok: false, text: '登录状态异常，请退出后重新登录' })
      return
    }
    setBusy(true)
    setMessage(null)
    try {
      const resp = await fetch(`${apiBase}/api/auth/huawei/bind/check-email`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify({ email: trimmed }),
      })
      const data = await resp.json().catch(() => ({} as any))
      if (!resp.ok) {
        throw new Error(data.message || data.error || `查询失败 (${resp.status})`)
      }
      setPassword('')
      setConfirm('')
      setStep(data.exists ? 'existing' : 'create')
    } catch (e) {
      setMessage({ ok: false, text: e instanceof Error ? e.message : '查询失败' })
    } finally {
      setBusy(false)
    }
  }

  /** 第二步提交：create=新邮箱设密完成注册；existing=验证已有账号密码并合并 */
  const handleSubmit = async () => {
    if (busy || !user) return
    const mode: 'create' | 'existing' = step === 'create' ? 'create' : 'existing'
    if (!email.trim() || !password) {
      setMessage({ ok: false, text: '请填写邮箱和密码' })
      return
    }
    if (mode === 'create') {
      if (!isPasswordCompliant(password)) {
        setMessage({ ok: false, text: PASSWORD_POLICY_HINT })
        return
      }
      if (password !== confirm) {
        setMessage({ ok: false, text: '两次输入的密码不一致' })
        return
      }
    }
    if (!accessToken) {
      setMessage({ ok: false, text: '登录状态异常，请退出后重新登录' })
      return
    }

    setBusy(true)
    setMessage(null)
    try {
      const resp = await fetch(`${apiBase}/api/auth/huawei/bind`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify({ mode, email: email.trim(), password }),
      })
      const data = await resp.json().catch(() => ({} as any))
      if (!resp.ok) {
        throw new Error(data.message || data.error || `绑定失败 (${resp.status})`)
      }

      if (mode === 'create') {
        // 创建新邮箱：会话不变，更新 user 状态与持久化后进入应用
        await persistUpdatedUser({ ...user, email: data.email, needsEmailBinding: false })
        navigate('/', { replace: true })
        return
      }

      // 绑定已有账号：换发会话（落地顺序与 useHandleCallback 对齐）
      const newUser: User = {
        id: data.user.id,
        email: data.user.email ?? '',
        name: data.user.name ?? '',
        nickname: data.user.nickname ?? '',
        avatar: data.user.avatar ?? '',
        needsEmailBinding: false,
      }
      try {
        await tauriApi.initUser(newUser.id, newUser.email, newUser.nickname || newUser.name || '')
      } catch (initError) {
        console.error('[EmailBinding] 本地数据库用户初始化失败:', initError)
      }

      setUser(newUser)
      if (data.accessToken && data.expiresIn) {
        await setTokens(data.accessToken, data.refreshToken || '', data.expiresIn)
      }

      try {
        await invoke('sync_current_session', { accessToken: data.accessToken })
      } catch (syncError) {
        console.warn('[EmailBinding] ArkTS 会话同步失败:', syncError)
      }

      reportHarmonyPushToken()
      navigate('/', { replace: true })
    } catch (e) {
      setMessage({ ok: false, text: e instanceof Error ? e.message : '绑定失败' })
    } finally {
      setBusy(false)
    }
  }

  if (!isAuthenticated || !user) {
    return null
  }

  return (
    // ★ 键盘遮挡修复（三版）：外层保持可滚动；keyboardUp 时内容从垂直居中改为
    //   顶部对齐（小顶距），保证聚焦输入框位于键盘上方
    <div className="min-h-screen bg-gray-50 overflow-y-auto">
      <div
        className={`min-h-full flex justify-center ${keyboardUp ? 'items-start pt-4' : 'items-center py-10'}`}
      >
        <div className="max-w-md w-full mx-4">
          <div className="bg-white rounded-2xl shadow-lg p-8">
            <div className="w-14 h-14 bg-blue-600 rounded-xl mx-auto mb-4 flex items-center justify-center">
              <svg className="w-7 h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2 text-center">跨端登录设置</h1>
            <p className="text-sm text-gray-500 mb-6 text-center">
              设置后可在网页 / 桌面端登录同一账号（数据、套餐全平台同步）
            </p>

            {step === 'email' && (
              <div className="space-y-3">
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  onFocus={handleInputFocus}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') handleCheckEmail()
                  }}
                  placeholder="邮箱"
                  autoComplete="email"
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                {/* ★ 真实邮箱提示：网页 / 桌面端靠该邮箱登录 */}
                <p className="text-xs text-amber-700 bg-amber-50 border border-amber-100 rounded-lg px-3 py-2 leading-relaxed">
                  请务必使用真实邮箱——网页 / 桌面端需用该邮箱登录，填写错误将无法跨端登录（之后可在「我的帐号」中更换）。
                </p>
                <button
                  onClick={handleCheckEmail}
                  disabled={busy}
                  className="w-full px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 active:bg-blue-800 transition-colors disabled:opacity-60"
                >
                  {busy ? '查询中...' : '下一步'}
                </button>
              </div>
            )}

            {step === 'create' && (
              <div className="space-y-3">
                <p className="text-sm text-gray-600">
                  邮箱：<span className="font-semibold text-gray-900">{email.trim()}</span>
                  <button
                    onClick={() => {
                      setStep('email')
                      setMessage(null)
                    }}
                    className="ml-2 text-xs text-blue-600 hover:underline"
                  >
                    返回修改
                  </button>
                </p>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  onFocus={handleInputFocus}
                  placeholder="设置密码（8 位以上，含大小写字母和数字）"
                  autoComplete="new-password"
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <input
                  type="password"
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  onFocus={handleInputFocus}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') handleSubmit()
                  }}
                  placeholder="确认密码"
                  autoComplete="new-password"
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <p className="text-xs text-gray-400">
                  {PASSWORD_POLICY_HINT}；为当前华为账号创建跨端登录邮箱，其它客户端可用邮箱密码登录
                </p>
                <button
                  onClick={handleSubmit}
                  disabled={busy}
                  className="w-full px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 active:bg-blue-800 transition-colors disabled:opacity-60"
                >
                  {busy ? '处理中...' : '完成注册'}
                </button>
              </div>
            )}

            {step === 'existing' && (
              <div className="space-y-3">
                <p className="text-sm text-amber-700 bg-amber-50 border border-amber-100 rounded-lg px-3 py-2 leading-relaxed">
                  该邮箱已注册账号。输入<b>该账号的密码</b>即可合并——合并后华为一键登录与邮箱密码登录进入同一账号（其数据会同步到此设备）。
                </p>
                <p className="text-sm text-gray-600">
                  邮箱：<span className="font-semibold text-gray-900">{email.trim()}</span>
                  <button
                    onClick={() => {
                      setStep('email')
                      setMessage(null)
                    }}
                    className="ml-2 text-xs text-blue-600 hover:underline"
                  >
                    返回修改
                  </button>
                </p>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  onFocus={handleInputFocus}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') handleSubmit()
                  }}
                  placeholder="该账号的密码"
                  autoComplete="current-password"
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <button
                  onClick={handleSubmit}
                  disabled={busy}
                  className="w-full px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 active:bg-blue-800 transition-colors disabled:opacity-60"
                >
                  {busy ? '处理中...' : '验证并合并'}
                </button>
              </div>
            )}

            {message && (
              <p className={`text-sm text-center mt-3 ${message.ok ? 'text-green-600' : 'text-red-600'}`}>
                {message.text}
              </p>
            )}

            {/* 强制设置：无"跳过"出口，仅提供退出登录 */}
            <button
              onClick={() => {
                useAuthStore.getState().logout()
              }}
              className="w-full py-2 mt-3 text-xs text-gray-400 hover:text-gray-600 transition-colors"
            >
              退出登录
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
